/**
 * Phone number formatting utilities.
 *
 * The backend stores all (non-corrupt) phone numbers in E.164 format
 * (e.g. "+31612345678"). The frontend should display them in a
 * locale-friendly format and always use E.164 in `tel:` hrefs.
 *
 * No external dependency (libphonenumber-js) is used — a small
 * formatter that handles common NL + generic international numbers
 * is enough for our current needs.
 */

const NL_MOBILE_PREFIX = '+316';
const NL_COUNTRY_CODE = '+31';
const NL_COUNTRY_DIGITS = '31';

const stripFormatting = (value) => {
  if (value == null) return '';
  return String(value).replace(/[^\d+]/g, '');
};

/**
 * Normalize a phone number to E.164.
 * Mirrors backend `phoneService.normalize` (NL-first, international pass-through).
 *
 * @returns {string|null} E.164 string (e.g. "+31612345678") or null if invalid
 */
export const normalizePhone = (raw, { defaultCountry = 'nl' } = {}) => {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');
  if (!digits) return null;
  const cleaned = hasPlus ? `+${digits}` : digits;

  if (cleaned.startsWith('+')) {
    const e164Digits = cleaned.slice(1);
    if (e164Digits.length < 6 || e164Digits.length > 15) return null;
    return `+${e164Digits}`;
  }

  if (cleaned.startsWith('00')) {
    const intl = cleaned.slice(2);
    if (intl.length < 6 || intl.length > 15) return null;
    return `+${intl}`;
  }

  if (defaultCountry !== 'nl') return null;

  if (cleaned.startsWith('0')) {
    const local = cleaned.slice(1);
    if (local.length !== 9) return null;
    return `+${NL_COUNTRY_DIGITS}${local}`;
  }

  if (cleaned.startsWith(NL_COUNTRY_DIGITS) && cleaned.length === 11) {
    return `+${cleaned}`;
  }

  if (cleaned.length === 9) {
    return `+${NL_COUNTRY_DIGITS}${cleaned}`;
  }

  return null;
};

/**
 * Digits-only E.164 for WhatsApp `wa.me` links (no leading +).
 * e.g. "0642507026" / "+31642507026" → "31642507026"
 */
export const toWhatsAppPhone = (raw) => {
  const e164 = normalizePhone(raw);
  if (!e164) return null;
  return e164.replace(/\D/g, '');
};

// ITU-T E.164 country-code length lookup (covers the common cases).
// 1-digit: NANP (1) and Russia/Kazakhstan (7).
// 3-digit: a curated set of known 3-digit prefixes.
// Everything else falls back to 2-digit.
const THREE_DIGIT_CC_PREFIXES = [
  '210', '211', '212', '213', '216', '218', '220', '221', '222', '223',
  '224', '225', '226', '227', '228', '229', '230', '231', '232', '233',
  '234', '235', '236', '237', '238', '239', '240', '241', '242', '243',
  '244', '245', '246', '248', '249', '250', '251', '252', '253', '254',
  '255', '256', '257', '258', '260', '261', '262', '263', '264', '265',
  '266', '267', '268', '269', '290', '291', '297', '298', '299', '350',
  '351', '352', '353', '354', '355', '356', '357', '358', '359', '370',
  '371', '372', '373', '374', '375', '376', '377', '378', '379', '380',
  '381', '382', '383', '385', '386', '387', '389', '420', '421', '423',
  '500', '501', '502', '503', '504', '505', '506', '507', '508', '509',
  '590', '591', '592', '593', '594', '595', '596', '597', '598', '599',
  '670', '672', '673', '674', '675', '676', '677', '678', '679', '680',
  '681', '682', '683', '685', '686', '687', '688', '689', '690', '691',
  '692', '850', '852', '853', '855', '856', '870', '880', '881', '886',
  '960', '961', '962', '963', '964', '965', '966', '967', '968', '970',
  '971', '972', '973', '974', '975', '976', '977', '992', '993', '994',
  '995', '996', '998',
];

const guessCountryCodeLength = (digits) => {
  if (!digits) return 0;
  const c1 = digits.charAt(0);
  if (c1 === '1' || c1 === '7') return 1;
  const first3 = digits.slice(0, 3);
  if (THREE_DIGIT_CC_PREFIXES.includes(first3)) return 3;
  return 2;
};

const chunkSubscriber = (subscriber) => {
  if (!subscriber) return [];
  // 10-digit NANP-style: 3-3-4
  if (subscriber.length === 10) {
    return [subscriber.slice(0, 3), subscriber.slice(3, 6), subscriber.slice(6)];
  }
  const groups = [];
  let cursor = 0;
  while (cursor < subscriber.length) {
    const remaining = subscriber.length - cursor;
    const size = remaining > 6 ? 3 : remaining;
    groups.push(subscriber.slice(cursor, cursor + size));
    cursor += size;
  }
  return groups;
};

/**
 * Return the raw E.164 form for use in `tel:` hrefs.
 * Falls back to the cleaned digits if no `+` prefix is present.
 */
export const toTelHref = (raw) => {
  const e164 = normalizePhone(raw);
  if (e164) return `tel:${e164}`;
  const cleaned = stripFormatting(raw);
  if (!cleaned) return '';
  return `tel:${cleaned}`;
};

/**
 * Format a phone number for display.
 *
 * - NL mobile (`+316XXXXXXXX`)        -> "06 12 34 56 78"
 * - NL fixed  (`+31XXXXXXXXX`)        -> "020 123 45 67"
 * - International (`+1...`, `+49...`) -> "+1 555 123 4567" (groups of 3-4)
 * - Already-local NL ("06...", "020...") stays readable
 * - Falls back to the input if formatting fails (e.g. obviously corrupt input)
 */
export const formatPhone = (raw) => {
  if (raw == null || raw === '') return '';
  const cleaned = stripFormatting(raw);
  if (!cleaned) return String(raw);

  if (cleaned.startsWith(NL_MOBILE_PREFIX) && cleaned.length === 12) {
    const local = '0' + cleaned.slice(3);
    return `${local.slice(0, 2)} ${local.slice(2, 4)} ${local.slice(4, 6)} ${local.slice(6, 8)} ${local.slice(8, 10)}`;
  }

  if (cleaned.startsWith(NL_COUNTRY_CODE) && cleaned.length >= 11 && cleaned.length <= 12) {
    const local = '0' + cleaned.slice(3);
    if (local.length === 10) {
      const area = local.slice(0, 3);
      const rest = local.slice(3);
      return `${area} ${rest.slice(0, 3)} ${rest.slice(3, 5)} ${rest.slice(5)}`.trim();
    }
    return local;
  }

  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1);
    const countryLen = guessCountryCodeLength(digits);
    const country = digits.slice(0, countryLen);
    const subscriber = digits.slice(countryLen);
    const groups = chunkSubscriber(subscriber);
    return `+${country}${groups.length ? ' ' + groups.join(' ') : ''}`.trim();
  }

  if (cleaned.startsWith('06') && cleaned.length === 10) {
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8, 10)}`;
  }
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
  }

  return String(raw);
};

/**
 * Loose validation. Accepts:
 *  - E.164 (starts with `+`, 8-15 digits)
 *  - NL local (starts with `0`, 10 digits)
 */
export const isValidPhone = (raw) => {
  return normalizePhone(raw) !== null;
};
